import { WrapConsentListProps, WrapSubCheckboxProps } from 'scbk-shared-module/modules/Terms';

type SetValueFn<T> = (key: keyof T, value: string) => void;
type GetValueFn<T> = (key: keyof T) => string;

export const TermsUtils = <T extends Record<string, any>>(
  setValue: SetValueFn<T>,
  getValues: GetValueFn<T>,
  list: WrapConsentListProps[],
  setAllTermsChecked: (v: boolean) => void,
) => {
  /**
   * usePageForm 의 setValue Wrapping
   * @param id
   * @param value
   */
  const setCheckedValue = (id: string, value: boolean) => {
    setValue(id as keyof T, value ? 'true' : 'false');
  };

  /**
   * 타입가드
   *  - hasCheckbox 속성은 WrapConsentListProps 타입에만 존재
   */
  const hasCheckboxField = (
    item: WrapSubCheckboxProps | WrapConsentListProps,
  ): item is WrapConsentListProps => {
    return 'hasCheckbox' in item;
  };

  /**
   * 체크박스 이벤트
   */
  const setChecked = ({
    index,
    subIndex,
    active,
  }: {
    index: number;
    subIndex?: number | undefined;
    active: boolean;
  }) => {
    const info: WrapSubCheckboxProps | WrapConsentListProps | undefined =
      subIndex !== undefined ? list[index]?.subCheckbox?.[subIndex] : list[index];

    if (!info) return;

    //if (hasCheckboxField(info) && !info.hasCheckbox) return;

    setCheckedValue(info.id ?? '', active);

    if (info.groupId) {
      const depth = getDepthFromId(info.id);

      if (depth === 1) {
        setGroupChecked(info.groupId, active);
      }

      if (depth === 2) {
        setGroupChecked(info.groupId, active);
      }

      if (depth === 3) {
        const checkboxList: WrapSubCheckboxProps[] = list[index].subCheckbox ?? [];

        if (isAllChecked(checkboxList)) {
          setCheckedValue(list[index].id, true);
        }

        if (isAllChecked(checkboxList)) {
          setGroupChecked(info.groupId, true);
        } else {
          list.forEach((item) => {
            if (item.groupId !== info.groupId) return;

            setCheckedValue(item.id, false);
          });
        }
      }
    }

    syncAllChecked();
  };

  const setAllChecked = (isChecked: boolean) => {
    const filteredList = list.filter((item) => item.hasCheckbox);
    filteredList.map((_, index) => {
      setChecked({ index, active: isChecked });
    });
  };

  const setGroupChecked = (groupId: string, value: boolean) => {
    list.forEach((item) => {
      if (item.groupId !== groupId) return;

      setCheckedValue(item.id, value);

      item.subCheckbox?.forEach((sub) => {
        setCheckedValue(sub.id, value);
      });
    });
  };

  const isAllChecked = (items: { id: string }[]) =>
    items.every((v) => getValues(v.id as keyof T) === 'true');

  const isAllUnchecked = (items: { id: string }[]) =>
    items.every((v) => getValues(v.id as keyof T) === 'false');

  const syncAllChecked = () => {
    const filtered = list.filter((item) => item.hasCheckbox);

    const isAll = filtered.every((v) => getValues(v.id as keyof T) === 'true');

    setAllTermsChecked(isAll);
  };

  const getDepthFromId = (id: string): number => {
    const base = id.split('.')[1];

    if (!base) return 0;

    return base.split('_').length;
  };

  return {
    setCheckedValue,
    hasCheckboxField,
    setChecked,
    setAllChecked,
    setGroupChecked,
    isAllChecked,
    isAllUnchecked,
    syncAllChecked,
    getDepthFromId,
  };
};
